
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, Play } from "lucide-react";

interface ActionButtonProps {
  children?: React.ReactNode; // Make children optional
  to?: string;
  onClick?: (e?: React.MouseEvent) => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "outline" | "back" | "play" | "floating";
  fullWidth?: boolean;
}

const ActionButton = ({
  children,
  to,
  onClick,
  disabled = false,
  variant = "primary",
  fullWidth = false
}: ActionButtonProps) => {
  const buttonStyles = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    back: "text-muted-foreground hover:text-foreground",
    play: "bg-primary/90 text-primary-foreground hover:bg-primary",
    floating: "bg-gradient-to-r from-[#8B5CF6] to-[#6366F1] text-white shadow-lg hover:shadow-xl"
  };

  const isBackButton = variant === "back";
  const isPlayButton = variant === "play";
  const isPrimaryButton = variant === "primary";
  const isFloatingButton = variant === "floating";

  // If this is a back button and it's disabled, don't render it at all
  if (isBackButton && disabled) {
    return null;
  }

  // Ensure we have button content even if no children provided
  const hasContent = Boolean(children) || isPlayButton;

  // Make sure children is not required for play buttons
  if (!hasContent && !isPlayButton) {
    console.warn('ActionButton should have children unless it is a play button');
  }

  const buttonClasses = `
    ${disabled ? "bg-gray-300 text-gray-500 cursor-not-allowed" : buttonStyles[variant]}
    ${fullWidth ? "w-full" : ""}
    ${isBackButton ? "text-sm p-2" : isPlayButton ? "p-2 rounded-full" : isFloatingButton ? "px-5 py-3 rounded-full" : "px-6 py-3"}
    inline-flex items-center justify-center rounded-md font-medium
    transition-colors focus-visible:outline-none focus-visible:ring-2
    focus-visible:ring-ring focus-visible:ring-offset-2
    ${isPrimaryButton ? "shadow-sm md:shadow-md hover:shadow-lg" : ""}
    ${isFloatingButton ? "animate-pulse-slow" : ""}
    ${disabled ? "opacity-70 pointer-events-none" : ""}
  `;

  const ButtonContent = (
    <motion.span
      className="relative flex items-center"
      whileTap={{ scale: disabled ? 1 : 0.98 }}
    >
      {isBackButton && <ChevronLeft className="mr-1" size={16} />}
      {isPlayButton && <Play className="h-4 w-4" />}
      {!isPlayButton && children}
    </motion.span>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={buttonClasses}>
        {ButtonContent}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
    >
      {ButtonContent}
    </button>
  );
};

export default ActionButton;
